import type { JSONContent } from "@tiptap/react";

/** Versioned TipTap JSON marker for the encounter `scratchpadNotes` string field. */
export const SCRATCHPAD_FORMAT_PREFIX = "stitch-scratchpad:v1:";

const EMPTY_DOC: JSONContent = {
  type: "doc",
  content: [{ type: "paragraph" }],
};

const BLOCK_TYPES = new Set([
  "paragraph",
  "heading",
  "blockquote",
  "bulletList",
  "orderedList",
  "listItem",
  "codeBlock",
  "horizontalRule",
]);

const INLINE_TYPES = new Set(["text", "hardBreak"]);

const MARK_TYPES = new Set(["bold", "italic", "strike", "code", "link"]);

/**
 * Walk a TipTap JSON document and return plain text for previews/consumers.
 */
export function extractPlainTextFromScratchpadDoc(doc: JSONContent): string {
  const blocks: string[] = [];

  const walkBlock = (node: JSONContent): string => {
    if (node.type === "text") {
      return node.text ?? "";
    }
    const children = node.content ?? [];
    if (
      node.type === "paragraph" ||
      node.type === "heading" ||
      node.type === "blockquote" ||
      node.type === "codeBlock"
    ) {
      return children.map(walkBlock).join("");
    }
    if (node.type === "bulletList" || node.type === "orderedList") {
      return children
        .map((item) => walkBlock(item))
        .filter((line) => line.length > 0)
        .join("\n");
    }
    if (node.type === "listItem") {
      return children.map(walkBlock).join("\n");
    }
    if (node.type === "hardBreak") {
      return "\n";
    }
    if (node.type === "horizontalRule") {
      return "";
    }
    if (children.length > 0) {
      return children.map(walkBlock).join("");
    }
    return "";
  };

  for (const child of doc.content ?? []) {
    const text = walkBlock(child);
    if (child.type === "horizontalRule") continue;
    blocks.push(text);
  }

  return blocks.join("\n").replace(/\n+$/u, "");
}

/**
 * Convert legacy plain-text scratchpad notes into a TipTap JSON document.
 */
export function plainTextToScratchpadDoc(value: string): JSONContent {
  const lines = value.split(/\r?\n/);
  return {
    type: "doc",
    content: lines.map((line) =>
      line
        ? { type: "paragraph", content: [{ type: "text", text: line }] }
        : { type: "paragraph" },
    ),
  };
}

/**
 * Serialize a TipTap document for persistence. Empty docs become "".
 */
export function serializeScratchpadDoc(doc: JSONContent): string {
  const text = extractPlainTextFromScratchpadDoc(doc).trim();
  if (text.length === 0) return "";
  return `${SCRATCHPAD_FORMAT_PREFIX}${JSON.stringify(doc)}`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isValidMarks(marks: unknown): boolean {
  if (marks === undefined) return true;
  if (!Array.isArray(marks)) return false;
  return marks.every((mark) => {
    if (!isPlainObject(mark) || typeof mark.type !== "string") return false;
    if (!MARK_TYPES.has(mark.type)) return false;
    if (mark.attrs !== undefined && !isPlainObject(mark.attrs)) return false;
    if (mark.type === "link") {
      const href = mark.attrs?.href;
      if (href !== undefined && typeof href !== "string") return false;
    }
    return true;
  });
}

function isValidTextNode(node: Record<string, unknown>): boolean {
  return (
    node.type === "text" &&
    typeof node.text === "string" &&
    isValidMarks(node.marks) &&
    node.content === undefined
  );
}

function isValidHardBreak(node: Record<string, unknown>): boolean {
  return node.type === "hardBreak" && node.content === undefined;
}

function isValidInlineNode(value: unknown): boolean {
  if (!isPlainObject(value) || typeof value.type !== "string") return false;
  if (!INLINE_TYPES.has(value.type)) return false;
  if (value.type === "text") return isValidTextNode(value);
  return isValidHardBreak(value);
}

function isValidHeadingAttrs(attrs: unknown): boolean {
  if (attrs === undefined) return true;
  if (!isPlainObject(attrs)) return false;
  const level = attrs.level;
  return (
    typeof level === "number" &&
    Number.isInteger(level) &&
    level >= 1 &&
    level <= 6
  );
}

function isValidBlockNode(value: unknown): boolean {
  if (!isPlainObject(value) || typeof value.type !== "string") return false;
  if (!BLOCK_TYPES.has(value.type)) return false;

  if (value.type === "horizontalRule") {
    return value.content === undefined;
  }

  if (value.type === "heading" && !isValidHeadingAttrs(value.attrs)) {
    return false;
  }

  if (
    value.attrs !== undefined &&
    value.type !== "heading" &&
    value.type !== "codeBlock" &&
    !isPlainObject(value.attrs)
  ) {
    return false;
  }

  if (value.type === "codeBlock") {
    if (value.attrs !== undefined && !isPlainObject(value.attrs)) return false;
    if (value.content === undefined) return true;
    if (!Array.isArray(value.content)) return false;
    return value.content.every(isValidInlineNode);
  }

  if (value.type === "bulletList" || value.type === "orderedList") {
    if (!Array.isArray(value.content)) return false;
    return value.content.every(
      (item) =>
        isPlainObject(item) &&
        item.type === "listItem" &&
        isValidBlockNode(item),
    );
  }

  if (value.type === "listItem") {
    if (!Array.isArray(value.content) || value.content.length === 0) {
      return false;
    }
    return value.content.every(isValidBlockNode);
  }

  if (
    value.type === "paragraph" ||
    value.type === "heading" ||
    value.type === "blockquote"
  ) {
    if (value.content === undefined) return true;
    if (!Array.isArray(value.content)) return false;
    if (value.type === "blockquote") {
      return value.content.every(isValidBlockNode);
    }
    return value.content.every(isValidInlineNode);
  }

  return false;
}

/**
 * Validate a StarterKit-compatible TipTap v1 document deeply enough that
 * `setContent` will not throw on unknown/malformed nodes.
 */
export function isValidScratchpadV1Doc(value: unknown): value is JSONContent {
  if (!isPlainObject(value) || value.type !== "doc") return false;
  if (value.content === undefined) return true;
  if (!Array.isArray(value.content)) return false;
  return value.content.every(isValidBlockNode);
}

/**
 * Load stored scratchpad notes into TipTap JSON. Unmarked values are legacy
 * plain text; never treat unmarked angle brackets as HTML.
 */
export function parseScratchpadToEditorContent(value: string): JSONContent {
  if (!value.trim()) return EMPTY_DOC;

  if (value.startsWith(SCRATCHPAD_FORMAT_PREFIX)) {
    const raw = value.slice(SCRATCHPAD_FORMAT_PREFIX.length);
    try {
      const parsed: unknown = JSON.parse(raw);
      if (isValidScratchpadV1Doc(parsed)) return parsed;
    } catch {
      // Fall through to plain-text recovery so corrupt payloads are not dropped.
    }
    return plainTextToScratchpadDoc(value);
  }

  return plainTextToScratchpadDoc(value);
}

/**
 * Plain-text projection for encounter list previews and other non-editor consumers.
 */
export function scratchpadNotesToPlainText(
  value: string | null | undefined,
): string {
  if (value == null || !value.trim()) return "";
  if (!value.startsWith(SCRATCHPAD_FORMAT_PREFIX)) return value;

  const raw = value.slice(SCRATCHPAD_FORMAT_PREFIX.length);
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isValidScratchpadV1Doc(parsed)) {
      return extractPlainTextFromScratchpadDoc(parsed);
    }
  } catch {
    return value;
  }
  return value;
}

export { EMPTY_DOC };
